"use client";

import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, Trash2, ArrowRight, Loader2, Gift, ShieldCheck, Boxes } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import GiftCodeRedeem from "@/components/cart/GiftCodeRedeem";
import GiftPurchaseModal from "@/components/cart/GiftPurchaseModal";
import PromoCountdown from "@/components/ui/PromoCountdown";
import BundleUpsell from "@/components/bundles/BundleUpsell";
import { DEFAULT_FOOTER, type PaymentLogo } from "@/lib/cms-types";

export default function CartSidebar() {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    clearCart,
    totalPrice,
    totalOriginalPrice,
    count,
  } = useCart();
  const router = useRouter();
  const [checkingOut, setCheckingOut] = useState(false);
  const [giftItem, setGiftItem] = useState<(typeof items)[number] | null>(null);
  const [paymentLogos, setPaymentLogos] = useState<PaymentLogo[]>(DEFAULT_FOOTER.paymentLogos);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/cms")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && Array.isArray(data?.footer?.paymentLogos)) {
          setPaymentLogos(data.footer.paymentLogos);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCheckout() {
    closeCart();
    router.push("/platforma/checkout");
  }

  const discount =
    totalOriginalPrice > totalPrice ? totalOriginalPrice - totalPrice : 0;

  const enabledLogos = paymentLogos.filter((l) => l.enabled && l.imageUrl);

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-[61] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <ShoppingCart size={20} className="text-primary" />
                <h2 className="text-lg font-bold text-dark-blue dark:text-white">
                  Koszyk{" "}
                  {count > 0 && (
                    <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                      ({count})
                    </span>
                  )}
                </h2>
              </div>
              <button
                onClick={closeCart}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                    <ShoppingCart size={28} className="text-slate-300 dark:text-slate-600" />
                  </div>
                  <p className="text-slate-500 dark:text-slate-300 font-medium mb-1">
                    Twój koszyk jest pusty
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-500">
                    Dodaj kursy, aby rozpocząć naukę
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 100 }}
                      className="flex gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-700/60 hover:border-slate-200 dark:hover:border-slate-600 transition-colors bg-white dark:bg-slate-800/60"
                    >
                      {/* Thumbnail */}
                      {item.thumbnail ? (
                        <img
                          src={item.thumbnail}
                          alt={item.title}
                          className="w-16 h-16 rounded-lg object-cover shrink-0"
                        />
                      ) : (
                        <div
                          className="w-16 h-16 rounded-lg shrink-0 flex items-center justify-center"
                          style={{
                            backgroundColor: `${item.cardColor || "#4f46e5"}20`,
                          }}
                        >
                          {item.kind === "bundle" ? (
                            <Boxes size={20} style={{ color: item.cardColor || "#7c3aed" }} />
                          ) : (
                            <ShoppingCart
                              size={20}
                              style={{ color: item.cardColor || "#4f46e5" }}
                            />
                          )}
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        {item.kind === "bundle" ? (
                          <>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                                ZESTAW
                              </span>
                              {item.originalPrice > item.price && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white">
                                  −{Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}%
                                </span>
                              )}
                            </div>
                            <h4 className="text-sm font-semibold text-dark-blue dark:text-white truncate">
                              {item.title}
                            </h4>
                            {((item.bundleCourses?.length ?? 0) + (item.bundleMaterials?.length ?? 0)) > 0 && (
                              <ul className="mt-1 space-y-0.5">
                                {[...(item.bundleCourses ?? []), ...(item.bundleMaterials ?? [])].map((c) => (
                                  <li
                                    key={c.id}
                                    className="flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400"
                                  >
                                    <span className="truncate">{c.title}</span>
                                    <span className="shrink-0 text-slate-400">{c.price.toFixed(0)} zł</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <div className="flex items-baseline gap-2 mt-1.5">
                              <span className="text-sm font-bold text-violet-700 dark:text-violet-300">
                                {item.price.toFixed(0)} zł
                              </span>
                              {item.originalPrice > item.price && (
                                <span className="text-xs text-slate-400 dark:text-slate-500 line-through">
                                  {item.originalPrice.toFixed(0)} zł
                                </span>
                              )}
                            </div>
                            {item.originalPrice > item.price && (
                              <p className="mt-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                Oszczędzasz {(item.originalPrice - item.price).toFixed(0)} zł
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <h4 className="text-sm font-semibold text-dark-blue dark:text-white truncate">
                              {item.title}
                            </h4>
                            <div className="flex items-baseline gap-2 mt-1">
                              <span className="text-sm font-bold text-dark-blue dark:text-white">
                                {item.price > 0
                                  ? `${item.price.toFixed(0)} zł`
                                  : "Za darmo"}
                              </span>
                              {item.originalPrice > item.price && (
                                <span className="text-xs text-slate-400 dark:text-slate-500 line-through">
                                  {item.originalPrice.toFixed(0)} zł
                                </span>
                              )}
                            </div>
                            {/* Promocja czasowa */}
                            {item.promoEndsAt && (
                              <div className="mt-1.5">
                                <PromoCountdown endsAt={item.promoEndsAt} variant="compact" />
                              </div>
                            )}
                            {/* Podaruj ten kurs (materiałów nie można podarować) */}
                            {item.kind !== "material" && (
                              <motion.button
                                onClick={() => setGiftItem(item)}
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                className="group/gift mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors cursor-pointer"
                              >
                                <motion.span
                                  className="inline-flex"
                                  initial={{ rotate: 0 }}
                                  whileHover={{ rotate: [0, -12, 12, -8, 0] }}
                                  transition={{ duration: 0.5 }}
                                >
                                  <Gift size={12} />
                                </motion.span>
                                Podaruj jako prezent
                              </motion.button>
                            )}
                          </>
                        )}
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors self-center"
                      >
                        <Trash2 size={16} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Sugestia zestawu — jeśli pojedyncze kursy tworzą dostępny pakiet */}
              {items.length > 0 && (
                <div className="mt-4">
                  <BundleUpsell variant="cart" />
                </div>
              )}

              {/* Aktywacja kodu prezentowego — zawsze widoczna */}
              <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-4">
                <GiftCodeRedeem variant="inline" onRedeemed={() => closeCart()} />
              </div>
            </div>
            {/* Footer — checkout area */}
            {items.length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-800 px-6 py-4 space-y-3 bg-slate-50 dark:bg-slate-950/60">
                {/* Price summary */}
                {discount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Oszczędzasz</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                      -{discount.toFixed(0)} zł
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-700 dark:text-slate-200 font-medium">Razem</span>
                  <div className="text-right">
                    <span className="text-2xl font-extrabold text-dark-blue dark:text-white">
                      {totalPrice.toFixed(0)} zł
                    </span>
                  </div>
                </div>

                {/* Checkout button */}
                <button
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-lg shadow-primary/20"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />{" "}
                      Przetwarzanie...
                    </>
                  ) : (
                    <>
                      Przejdź do płatności{" "}
                      <ArrowRight
                        size={18}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </>
                  )}
                </button>

                <div className="pt-1 space-y-2.5">
                  <p className="flex items-center justify-center gap-1.5 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    <ShieldCheck size={14} />
                    Bezpieczna płatność
                  </p>
                  {enabledLogos.length > 0 && (
                    <div className="flex items-center justify-center gap-2">
                      {enabledLogos.map((logo) => (
                        <span
                          key={logo.id}
                          className="inline-flex items-center rounded-md bg-white px-2 py-1 ring-1 ring-slate-200 dark:ring-slate-700"
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={logo.imageUrl}
                            alt={logo.name}
                            title={logo.name}
                            loading="lazy"
                            className="h-4 w-auto object-contain"
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
      </AnimatePresence>

      {/* Modal zakupu kursu jako prezent */}
      {giftItem && (
        <GiftPurchaseModal
          open={!!giftItem}
          onClose={() => setGiftItem(null)}
          course={{
            id: giftItem.id,
            title: giftItem.title,
            slug: giftItem.slug,
            price: giftItem.price,
            originalPrice: giftItem.originalPrice,
            thumbnail: giftItem.thumbnail,
            cardColor: giftItem.cardColor,
          }}
        />
      )}
    </>
  );
}
